// tslint:disable:forin
// tslint:disable: no-output-on-prefix

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { BaseAppDetailMenuComponentInput } from "../../ng-app-details/ng-app-details-menu";
import { BaseConnectionMenuComponentInput } from "../../ng-connections/ng-connections-menu";
import { NgWaitService } from "../../ng-shared/ng-components/ng-wait/ng-wait.service";
import { NgToolsService } from "../../ng-shared/ng-util/ng-tools.service";

import { Subscription } from "rxjs";
import { BaseEnvToolsAgentCreateMenuComponentInput } from "../../ng-env-tools/base-agent/base-agent-menu";

/**
 * Class BaseAppHmInlineComponent
 * This class encapsulates the required UI template and functionality for an inlime hamburger menu option
 * which does not open a popup
 * @param @Input() {input} Menu Item input from app list or app details
 * @param @Output() {onClick} Base inline component output event emitter
 * @param @Input() {label} Menu item label to be set by tenant to override, default is provided by TCI for the given menu item
 * @param @Input() {uiBlockedText} Text to display during UI blocking
 * @param @Input() {uiBlocked} Toggle to turn on/off UI blocking
 * @param @Output() {uiBlockedChange} Event Notification for tenant to do something on {uiBlocked} state change
 * @param @Input() {toastrText} Toastr message to show
 * @param @Input() {toastrShow} Toggle to enable disable toast message output
 * @param @Output() {toastrShowChange} Event Notification for tenant to do something on {toastrShow} state change
 * @param @Input() {enabled}  To enable the menu item
 * @param @Output() {enabledChange} to emit the enabled change
 * @param @Input() {toastrError} To indicate the toastr msg is an error message
 * @param @Output() {toastrErrorChange} to emit toastError change
 */
@Component({
    selector: "ng-base-hm-inline-component",
    template: `
        <a *ngIf="enabled" id="{{id}}" class="hm-inline {{ iconClassName ? 'hm-item-ic hm-item-ic-' + iconClassName : '' }}" (click)="handleClick($event)">{{label}}</a>
        <a *ngIf="!enabled" id="{{id}}" class="hm-inline menudisabled {{ iconClassName ? 'hm-item-ic hm-item-ic-' + iconClassName : '' }}">{{label}}</a>
    `,
})
export class BaseAppHmInlineComponent implements OnInit, OnChanges, OnDestroy {
    @Input() input: BaseAppDetailMenuComponentInput | BaseConnectionMenuComponentInput | BaseEnvToolsAgentCreateMenuComponentInput;
    @Output() onClick: EventEmitter<null> = new EventEmitter();

    @Input() label: string;

    @Input() uiBlockedText = "";
    @Input() uiBlocked = false;
    @Output() uiBlockedChange: EventEmitter<boolean> = new EventEmitter();

    @Input() toastrText = "";
    @Input() toastrShow = false;
    @Output() toastrShowChange: EventEmitter<boolean> = new EventEmitter();

    @Input() toastrError = false;
    @Output() toastrErrorChange: EventEmitter<boolean> = new EventEmitter();

    @Input() enabled = true;
    @Output() enabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    private blockSubs: Subscription;
    private iconClassName: string;
    private id: string;

    /**
     * Constructor
     * @param translate
     * @param toastrSrv
     * @param uiBlockSrv
     * @param toolsSrv
     */
    constructor(
        private translate: TranslateService,
        private toastrSrv: NgToastrService,
        private uiBlockSrv: NgWaitService,
        private toolsSrv: NgToolsService
    ) {
        this.blockSubs = uiBlockSrv.getBlockSubject()
            .filter(state => state.invokerId === this.compId)
            .subscribe(state => {
                this.uiBlockedChange.emit(state.blocked);
                this.uiBlocked = state.blocked;
            });
    }

    ngOnInit() {
        if (this.isInputApp()) { // Icons only for applications
            // @ts-ignore
            this.iconClassName = this.input.componentOption.getRouteId();
        }

        // @ts-ignore inputObj from agent (BaseEnvToolsAgentCreateMenuComponentInput) doesn't have id and name property
        this.id = this.input.componentSelector.toString() + "-" + (this.inputObj.id || this.inputObj.getSelection().getRouteId());
    }

    ngOnChanges(changes: SimpleChanges): void {
        for (const propName in changes) {
            if (changes[propName].isFirstChange()) {
                return;
            }

            const chng = changes[propName].currentValue;

            switch (propName) {
                case "uiBlocked":
                    if (!!chng) {
                        this.uiBlockSrv.block(this.uiBlockedText, this.compId);
                    } else {
                        this.uiBlockSrv.unblock(this.compId);
                    }
                    break;

                case "toastrShow":
                    if (!!chng) {
                        if (this.toastrError) {
                            // @ts-ignore
                            this.toastrSrv.error(this.inputObj.name, this.toastrText);
                        } else {
                            // @ts-ignore
                            this.toastrSrv.success(this.inputObj.name, this.toastrText);
                        }
                        this.toolsSrv.setTimeout(() => { // Update local toastr boolean
                            this.toastrShow = false;
                            this.toastrShowChange.emit(this.toastrShow);

                        }, this.toastrSrv.getLife());
                    } else {
                        this.toastrSrv.clear();
                    }
                    break;
            }
        }
    }

    ngOnDestroy() {
        this.blockSubs.unsubscribe();
    }

    /**
     * An identifier to filter out blocker UI responses
     */
    private get compId(): string {
        // @ts-ignore
        return this.input && this.inputObj && this.inputObj.id ?
            // @ts-ignore
            this.inputObj.id :
            (Math.random() * 9999).toString();
    }

    private handleClick(event) {
        this.onClick.emit(null);
    }

    protected isBlocked(): boolean {
        return this.uiBlocked;
    }

    protected blockUI() {
        this.uiBlocked = true;
    }

    protected unblockUI() {
        this.uiBlocked = false;
    }

    private isInputApp(): boolean {
        return this.input instanceof BaseAppDetailMenuComponentInput;
    }

    private isInputConnection(): boolean {
        return this.input instanceof BaseConnectionMenuComponentInput;
    }

    private isInputAgent(): boolean {
        return this.input instanceof BaseEnvToolsAgentCreateMenuComponentInput;
    }

    private get inputObj() {
        if (this.isInputApp()) {
            return (this.input as BaseAppDetailMenuComponentInput).application;
        }

        if (this.isInputConnection()) {
            return (this.input as BaseConnectionMenuComponentInput).connection;
        }

        if (this.isInputAgent()) {
            return (this.input as BaseEnvToolsAgentCreateMenuComponentInput);
        }

        throw Error("The input instance is not supported");
    }
}

